import { Box, Dialog, Spinner, Text } from "@primer/react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { authApi } from "../api/auth/authApi";
import { NotifyHelper } from "../helpers/toast";
import { useAuth } from "../hooks/useAuth";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Modal RESET mật khẩu sinh viên — gọi POST /api/auth/dev-reset-password.
 * CHỈ phiên đăng nhập bằng mật khẩu dev (claim dev_password=true) mới hiện được modal này
 * (Header gate qua isDevSession); backend gate thêm bằng cờ DevAuth.AllowPasswordReset.
 * KHÔNG cần mật khẩu cũ — luồng hỗ trợ admin reset hộ SV quên mật khẩu. id_sv lấy từ JWT
 * (SV đang được impersonate).
 *
 * Mirror ChangePasswordDialog: dùng Primer Dialog legacy + createPortal(document.body)
 * để escape stacking context của Header sticky.
 */
export const DevResetPasswordDialog = ({ isOpen, onClose }: IProps) => {
    const { user } = useAuth();
    const [newPass, setNewPass] = useState("");
    const [rePass, setRePass] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showRe, setShowRe] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const reset = () => {
        setNewPass("");
        setRePass("");
        setShowNew(false);
        setShowRe(false);
        setErrorMsg("");
        setIsSubmitting(false);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        reset();
        onClose();
    };

    const validate = (): string | null => {
        if (!newPass) return "Vui lòng nhập mật khẩu mới.";
        if (!rePass) return "Vui lòng nhập lại mật khẩu mới.";
        if (newPass.length < 6) return "Mật khẩu mới phải có ít nhất 6 ký tự.";
        if (newPass !== rePass) return "Nhập lại mật khẩu mới chưa khớp.";
        return null;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const err = validate();
        if (err) {
            setErrorMsg(err);
            return;
        }
        setErrorMsg("");
        setIsSubmitting(true);
        const res = await authApi.DevResetPassword({
            mat_khau_moi: newPass,
            mat_khau_moi_nhap_lai: rePass,
        });
        setIsSubmitting(false);
        if (res.is_success) {
            NotifyHelper.Success(res.data?.message || "Reset mật khẩu thành công.");
            reset();
            onClose();
        } else {
            setErrorMsg(res.message || "Reset mật khẩu thất bại. Vui lòng thử lại.");
        }
    };

    const renderField = (
        label: string,
        value: string,
        onChange: (v: string) => void,
        show: boolean,
        toggleShow: () => void,
        autoFocus = false
    ) => (
        <Box sx={{ my: 2 }}>
            <Text
                as="p"
                sx={{ mb: "5px", fontWeight: "bold", fontSize: 14, color: "#333" }}
            >
                {label} <Text sx={{ color: "red" }}>(*)</Text>
            </Text>
            <div className="old-show-hide-password">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isSubmitting}
                    autoFocus={autoFocus}
                />
                <div className="input-group-addon">
                    <button
                        type="button"
                        onClick={toggleShow}
                        aria-label={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        <i
                            className={`fa ${show ? "fa-eye" : "fa-eye-slash"}`}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>
        </Box>
    );

    return createPortal(
        <Dialog
            isOpen={isOpen}
            onDismiss={handleClose}
            aria-labelledby="dev-reset-password-title"
            sx={{ width: 480, maxWidth: "92vw" }}
        >
            <Dialog.Header id="dev-reset-password-title">
                Reset mật khẩu sinh viên (hỗ trợ)
            </Dialog.Header>
            <Box
                as="form"
                onSubmit={handleSubmit}
                sx={{ p: 3 }}
                onKeyUp={(e) => {
                    if (e.key === "Enter" && !isSubmitting) handleSubmit();
                }}
            >
                <div
                    className="old-alert"
                    style={{
                        background: "#fff3cd",
                        borderColor: "#ffeeba",
                        color: "#856404",
                        marginBottom: 8,
                    }}
                >
                    Đặt lại mật khẩu cho{" "}
                    <strong>
                        {user?.ma_sv} – {user?.ho_ten}
                    </strong>{" "}
                    mà không cần mật khẩu cũ. Thao tác được ghi log hệ thống.
                </div>

                <Text as="p" sx={{ fontSize: 1, color: "#666", mb: 2 }}>
                    Mật khẩu mới tối thiểu 6 ký tự, khuyến nghị có cả chữ, số và ký tự đặc biệt.
                </Text>

                {renderField("Mật khẩu mới", newPass, setNewPass, showNew, () => setShowNew((v) => !v), true)}
                {renderField("Nhập lại mật khẩu mới", rePass, setRePass, showRe, () => setShowRe((v) => !v))}

                {errorMsg && (
                    <div
                        className="old-alert"
                        style={{
                            background: "#f8d7da",
                            borderColor: "#f5c6cb",
                            color: "#721c24",
                            marginTop: 8,
                            marginBottom: 8,
                        }}
                    >
                        {errorMsg}
                    </div>
                )}

                <Box sx={{ my: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <button
                        type="button"
                        className="old-btn"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        style={{
                            padding: "8px 16px",
                            background: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            cursor: isSubmitting ? "not-allowed" : "pointer",
                        }}
                    >
                        Hủy
                    </button>
                    <button
                        type="submit"
                        className="old-btn old-btn-primary"
                        disabled={isSubmitting || !newPass || !rePass}
                        style={{ padding: "8px 16px", fontWeight: "bold" }}
                    >
                        {isSubmitting ? <Spinner size="small" /> : "Reset mật khẩu"}
                    </button>
                </Box>
            </Box>
        </Dialog>,
        document.body
    );
};

export default DevResetPasswordDialog;
